/** @file Setup-pose (FK) world of every document bone, parents first, through localToWorld. */
import type { Doc, LocalTransform } from '@pose2d/format/types';
import { coreFail } from '../_internal/coreFail';
import { POSE_LAYOUT } from '../contracts/pose';
import { INHERIT_CODES } from './inheritCodes';
import { localToWorld } from './localToWorld';

/** One bone's setup transform as a LOCAL_STRIDE block in POSE_LAYOUT order (what localToWorld reads). */
function setupBlock(setup: LocalTransform): Float64Array {
  const L = POSE_LAYOUT;
  const out = new Float64Array(L.LOCAL_STRIDE);
  out[L.X] = setup.x;
  out[L.Y] = setup.y;
  out[L.ROTATION] = setup.rotation;
  out[L.SCALE_X] = setup.scaleX;
  out[L.SCALE_Y] = setup.scaleY;
  out[L.SHEAR_X] = setup.shearX;
  out[L.SHEAR_Y] = setup.shearY;
  return out;
}

/**
 * Bone id → setup world [a, b, c, d, tx, ty] (W2-14): FK only (no IK, no animation), every inherit mode through
 * localToWorld, a root against a null parent, parents first by recursion. A parent loop throws CYCLE (params.path =
 * the id chain); a parent id missing from doc.bones throws NOT_FOUND. Allocates: not a per-frame call.
 */
export function docSetupWorlds(doc: Doc): Map<string, Float64Array> {
  const worlds = new Map<string, Float64Array>();
  const visiting: string[] = [];
  const visit = (id: string, child: string): Float64Array => {
    const known = worlds.get(id);
    if (known !== undefined) return known;
    if (visiting.includes(id)) {
      const loop = [...visiting, id].join(' -> ');
      coreFail('CYCLE', `cycle detected: ${loop}`, { params: { path: loop }, path: `bones.${child}.parent` });
    }
    const bone = doc.bones[id];
    if (bone === undefined) coreFail('NOT_FOUND', `bone "${id}" not found`, { params: { kind: 'bone', ref: id }, path: `bones.${child}.parent` });
    visiting.push(id);
    const parent = bone.parent === null ? null : visit(bone.parent, id);
    visiting.pop();
    const world = new Float64Array(POSE_LAYOUT.WORLD_STRIDE);
    localToWorld(world, setupBlock(bone.setup), 0, parent, INHERIT_CODES[bone.inherit]);
    worlds.set(id, world);
    return world;
  };
  for (const id of Object.keys(doc.bones)) visit(id, id);
  return worlds;
}
